// ActionsPanel — right rail in Command Center.
//
// This is the Zendesk-killer: Haiku reads the thread + Shopify context and
// proposes 3 ranked next moves. One keystroke (1 / 2 / 3) fires the whole
// action: sends the pre-drafted reply AND applies side effects (set status,
// add label, add Shopify order note, snooze until a date).
//
// Layout matches v2 mockup's `.actions-panel`:
//   eyebrow "Do one of these"
//   Larken display headline with italic accent
//   why paragraph
//   primary action card (with preview + 2 CTAs)
//   secondary action cards (×2, compact)
//   divider
//   keyboard hint row
//   divider
//   "Context I used" footer
//
// If Haiku hasn't responded yet: show a skeleton shimmer with 3 action card
// placeholders so the panel never feels broken. If Haiku errors: show a
// graceful fallback with a "Write a custom reply" button that hands focus
// to the sticky Composer at the bottom of the work column.
//
// Polish PR (Phase 1 #1): the old "Or write from scratch" Composer drawer
// and the "Tweak before sending" Composer drawer used to each render a
// second cramped textarea inside this panel. Kimi found that confusing
// (two composers on one screen) and the rail width never had enough room
// for real typing. Now both flows defer to the single sticky Composer:
//   • "Or write from scratch" / `C`  → focus the sticky composer.
//   • "Tweak" on an action card       → seed the sticky composer with the
//                                       Haiku preview and focus it.
//
// Polish #10 — the Slack "Message my team" drawer used to live here behind
// an `S` keypress. It is now replaced by the always-visible TeamPingPanel
// that Center.tsx mounts directly below this panel. The `S` shortcut moved
// to Center too, where it focuses that panel's textarea. ActionsPanel no
// longer owns any Slack affordance.

import { useEffect } from 'react';
import {
  useProposedActions,
  useExecuteAction,
  type ProposedAction,
} from '@/lib/hooks/useProposedActions';
import { ActionCard } from './ActionCard';

interface ActionsPanelProps {
  conversationId: string;
  brand: 'md' | 'hk';
  channel: string;
  /** Called after a successful action fire so the Center can auto-advance. */
  onCompleted: () => void;
  /**
   * Ask the parent (Center page) to focus the sticky Composer. Used by the
   * "Or write from scratch" button and the `C` shortcut.
   */
  onRequestCompose: () => void;
  /**
   * Ask the parent to pre-fill the sticky Composer with a proposed-action
   * preview before focusing it. Used by the Tweak button on ActionCard.
   */
  onSeedCompose: (text: string) => void;
}

export function ActionsPanel({
  conversationId,
  brand: _brand,
  channel,
  onCompleted,
  onRequestCompose,
  onSeedCompose,
}: ActionsPanelProps) {
  const { data, isLoading, error, refetch } = useProposedActions(conversationId);
  const exec = useExecuteAction(conversationId);

  const proposals = data?.proposals ?? [];
  const busy = exec.isPending;

  // Keyboard: 1 / 2 / 3 fire the corresponding proposal. C focuses the sticky
  // composer. (S now lives on Center and focuses TeamPingPanel — Polish #10.)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t?.isContentEditable ?? false)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === '1' && proposals[0]) {
        e.preventDefault();
        fire(proposals[0]);
      } else if (e.key === '2' && proposals[1]) {
        e.preventDefault();
        fire(proposals[1]);
      } else if (e.key === '3' && proposals[2]) {
        e.preventDefault();
        fire(proposals[2]);
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        onRequestCompose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, onRequestCompose]);

  function fire(action: ProposedAction) {
    if (busy) return;
    exec.mutate(
      { actionId: action.id },
      {
        onSuccess: () => onCompleted(),
      },
    );
  }

  // "Tweak" button on an ActionCard hands the preview off to the sticky
  // composer so Kimi can edit it in the big textarea instead of the cramped
  // right-rail drawer we used to open here.
  function handleTweak(action: ProposedAction) {
    onSeedCompose(action.preview);
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <aside className="actions-panel" aria-label="Suggested actions">
        <div className="eyebrow">Do one of these</div>
        <h3 className="display">
          Close the loop <em>fast</em>.
        </h3>
        <p className="why">Reading the thread…</p>
        <div className="action-card skeleton" />
        <div className="action-card skeleton sm" />
        <div className="action-card skeleton sm" />
      </aside>
    );
  }

  // Error fallback. Pinging the team now happens in TeamPingPanel below
  // this panel, so we don't need a second affordance here.
  if (error) {
    return (
      <aside className="actions-panel" aria-label="Suggested actions">
        <div className="eyebrow">Do one of these</div>
        <h3 className="display">
          Close the loop <em>fast</em>.
        </h3>
        <p className="why">
          Couldn't generate suggestions right now. Write your own in the
          composer below, or use the team-ping panel underneath to loop in
          your team. Hit <span className="kbd-inline">?</span> any time to
          see every shortcut.
        </p>
        <button type="button" className="btn primary" onClick={onRequestCompose}>
          Write a reply
        </button>
        <button
          type="button"
          className="btn sm"
          style={{ marginTop: 8 }}
          onClick={() => void refetch()}
        >
          Try again
        </button>

        <div className="divider-soft" />
        <div className="actions-secondary">
          <button
            type="button"
            className="actions-freecompose"
            onClick={onRequestCompose}
            aria-label="Write a custom reply"
          >
            Or write from scratch <span className="kbd">C</span>
          </button>
        </div>
      </aside>
    );
  }

  const [primary, ...secondaries] = proposals;

  return (
    <aside className="actions-panel" aria-label="Suggested actions">
      <div className="eyebrow">Do one of these</div>
      <h3 className="display">
        Close the loop <em>fast</em>.
      </h3>
      <p className="why">
        I read the thread and pulled {proposals.length} moves you could make right now. Keyboard:
        press 1 for the top pick. Hit <span className="kbd-inline">?</span> any time to see every shortcut.
      </p>

      {primary ? (
        <ActionCard action={primary} variant="primary" index={1} onFire={fire} onTweak={handleTweak} busy={busy} />
      ) : null}

      {secondaries.map((a, i) => (
        <ActionCard
          key={a.id}
          action={a}
          variant="secondary"
          index={i + 2}
          onFire={fire}
          onTweak={handleTweak}
          busy={busy}
        />
      ))}

      <div className="divider-soft" />
      <div className="actions-secondary">
        <button
          type="button"
          className="actions-freecompose"
          onClick={onRequestCompose}
          aria-label="Write a custom reply"
        >
          Or write from scratch <span className="kbd">C</span>
        </button>
      </div>

      {data?.context_note ? (
        <>
          <div className="divider-soft" />
          <div>
            <div className="eyebrow">Context I used</div>
            <p className="body-s context-note">{data.context_note}</p>
          </div>
        </>
      ) : null}

      {channel !== 'gmail' ? (
        <p className="body-s" style={{ marginTop: 16, opacity: 0.6, fontStyle: 'italic' }}>
          Sending on {channel} isn't wired yet. Actions land in a later build.
        </p>
      ) : null}
    </aside>
  );
}
